<template>
  <el-form :model="form" class="filters__form" label-position="top">
    <div class="main-filters__container">
      <el-form-item class="search-form-item" label="Search">
        <el-input
          v-if="isFilterEnabled('search')"
          v-model="form.search"
          data-testid="searchInput"
          prefix-icon="el-icon-search"
          placeholder="Search"
          clearable
          @input="emitChanges"
        />
      </el-form-item>

      <el-form-item label="Location">
        <multi-select
          v-if="isFilterEnabled('location')"
          v-model="form.location"
          placeholder="Choose location"
          allow-create
          filterable
          all-button
          :choices="locationChoices"
          :select-preset-choices="presetLocationChoices"
          select-preset-choices-label="CEE"
          @change="emitChanges"
        />
      </el-form-item>

      <el-form-item v-if="isFilterEnabled('sectors')" label="Sectors">
        <multi-select
          v-model="form.sector"
          placeholder="Choose sectors"
          filterable
          :choices="sectorChoices"
          @change="emitChanges"
        />
      </el-form-item>

      <el-form-item
        v-if="isFilterEnabled('companyStages')"
        label="Company stage"
        prop="stage"
      >
        <multi-select
          v-model="form.stage"
          placeholder="Choose stages"
          all-button
          :choices="stageChoices"
          @change="emitChanges"
        />
      </el-form-item>
    </div>
    <el-collapse ref="collapse" @change="showFilters">
      <el-collapse-item title="Show Advanced Filters" name="filters">
        <div class="filters__row">
          <el-form-item
            v-if="isFilterEnabled('annualRevenue')"
            label="Annual Revenue (EUR)"
          >
            <el-slider
              v-model="form.annual_revenue"
              range
              show-stops
              :max="9"
              :format-tooltip="formatRounds"
              @change="emitChanges"
            />
          </el-form-item>

          <el-tooltip content="Monthly Recurring Revenue" placement="top">
            <el-form-item v-if="isFilterEnabled('mrr')" label="MRR (EUR)">
              <el-slider
                v-model="form.monthly_recurring_revenue"
                range
                show-stops
                :max="9"
                :format-tooltip="formatMRR"
                @change="emitChanges"
              />
            </el-form-item>
          </el-tooltip>

          <el-form-item
            v-if="isFilterEnabled('productTypes')"
            label="Product type"
            prop="productTypes"
          >
            <multi-select
              v-model="form.product_type"
              placeholder="Choose types"
              :choices="productTypes"
              all-button
              @change="emitChanges"
            />
          </el-form-item>

          <el-form-item
            v-if="isFilterEnabled('businessTypes')"
            label="Business types"
          >
            <multi-select
              v-model="form.business_type"
              :choices="businessTypes"
              all-button
              @change="emitChanges"
            />
          </el-form-item>
        </div>
        <div class="filters__row">
          <el-form-item
            v-if="isFilterEnabled('previousRounds')"
            label="Previous rounds (EUR)"
          >
            <el-slider
              v-model="form.previous_rounds_size"
              range
              show-stops
              :max="9"
              :format-tooltip="formatRounds"
              @change="emitChanges"
            />
          </el-form-item>

          <el-form-item
            v-if="isFilterEnabled('currentRound')"
            label="Round size (EUR)"
          >
            <el-slider
              v-model="form.round_size"
              range
              show-stops
              :max="9"
              :format-tooltip="formatRounds"
              @change="emitChanges"
            />
          </el-form-item>

          <el-form-item
            :class="{ hidden: !user.isInvestor }"
            label="Interest State"
          >
            <multi-select
              v-model="form.interest_state"
              :choices="interestStatesChoices"
              all-button
              @change="emitChanges"
            />
          </el-form-item>

          <el-form-item
            v-if="isFilterEnabled('publishState')"
            :class="{ hidden: !user.isAdmin }"
            label="Shared"
          >
            <multi-select
              v-model="form.shared_with_all_organizations"
              :choices="sharedChoices"
              all-button
              placeholder="Choose share state"
              @change="emitChanges"
            />
          </el-form-item>

          <el-form-item
            v-if="user.isAdmin"
            class="is-fullwidth"
            label="Has documents"
          >
            <multi-select
              v-model="form.has_documents"
              :choices="hasDocumentsChoices"
              all-button
              @change="emitChanges"
            />
          </el-form-item>

          <el-form-item
            v-if="isFilterEnabled('profileProgress') && user.isAdmin"
            label="Profile progress"
          >
            <el-slider
              v-model="form.profile_progress"
              range
              show-stops
              :step="10"
              :format-tooltip="(val) => `${val}%`"
              @change="emitChanges"
            />
          </el-form-item>

          <el-form-item v-if="user.isAdmin" label="" />
          <el-form-item v-if="user.isAdmin" label="" />
          <el-form-item v-if="user.isAdmin" label="Sort by">
            <el-select
              v-model="form.ordering"
              clearable
              placeholder="Select order"
              @change="emitChanges"
            >
              <el-option
                v-for="item in orderingOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="filters-actions">
          <el-form-item
            v-show="user.isAdmin"
            v-if="isFilterEnabled('adminActions')"
          >
            <slot name="admin-actions">
              <el-button type="primary" @click="openStartupCreateModal()">
                Add startup
              </el-button>
              <startup-create-modal
                :show="startupCreateModalVisible"
                @close="closeStartupCreateModal()"
                @created="onStartupCreated()"
              />
            </slot>
          </el-form-item>

          <div class="flex items-center justify-between w-full">
            <div class="flex flex-1">
              <el-form-item
                v-show="user.isInvestorOrOrganizer"
                class="flex items-center !w-auto"
              >
                <span slot="label" class="el-form-item__label mb-0 mt-1 mr-3"
                  >Show only</span
                >
                <div class="checkbox">
                  <label>
                    <input
                      v-model="form.visited"
                      type="checkbox"
                      name="optionsRadios"
                      @change="emitChanges"
                    />
                    <span>Undiscovered</span>
                  </label>
                </div>
              </el-form-item>

              <el-form-item
                v-show="user.isInvestorOrOrganizer"
                class="flex items-center !w-auto"
              >
                <div class="checkbox">
                  <label>
                    <input
                      v-model="form.rated"
                      type="checkbox"
                      @change="emitChanges"
                    />
                    <span>Rated</span>
                  </label>
                </div>
              </el-form-item>

              <el-form-item
                v-show="user.isInvestorOrOrganizer"
                class="flex items-center !w-auto"
              >
                <div class="checkbox">
                  <label>
                    <input
                      v-model="form.unrated_only"
                      type="checkbox"
                      @change="emitChanges"
                    />
                    <span>Not rated</span>
                  </label>
                </div>
              </el-form-item>
            </div>

            <el-form-item
              v-if="isFilterEnabled('extraActions')"
              class="filters-extra"
            >
              <a
                href
                class="filters-action-link"
                @click.prevent="collapseFilters"
              >
                <i class="ion-chevron-up" />
                Hide filters
              </a>

              <a class="filters-action-link" href @click.prevent="resetFilters">
                <i class="ion-android-close" />
                Clear all
              </a>
            </el-form-item>
          </div>
        </div>
      </el-collapse-item>
    </el-collapse>
  </el-form>
</template>

<script>
import { mapState } from 'vuex'
import { sectorChoices } from '@/services/sectors'
import { COUNTRY_CHOICES } from '@/services/cities'
import { stages as stageChoices } from '@/services/startup-stages'
import { productTypeChoices as productTypes } from '@/services/product-types'
import { businessTypeChoices as businessTypes } from '@/services/business-types'
import { interestStatesChoices } from '@/services/interest-states'
import { roundSizeChoices as roundChoices } from '@/services/product-rounds'
import { monthlyRecurringRevenueChoices } from '@/services/monthly-recurring-revenue'
import StartupCreateModal from './StartupCreateModal.vue'
import MultiSelect from '@/components/MultiSelect.vue'
import { fromPairs } from 'lodash'
import { emptyFilterForm } from '@/store/startup-list'
import { getCorespondingDataArray } from '@/utils/filters'

export default {
  components: { StartupCreateModal, MultiSelect },
  props: {
    enabledFilters: { type: [Boolean, Array], default: () => true },
  },
  data() {
    return {
      form: {
        search: '',
        location: [],
        sector: [],
        stage: [],
        previous_rounds_size: [0, 9],
        round_size: [0, 9],
        product_type: [],
        business_type: [],
        annual_revenue: [0, 9],
        monthly_recurring_revenue: [0, 9],
        shared_with_all_organizations: [],
        has_documents: [],
        interest_state: [],
        visited: false,
        profile_progress: [0, 100],
        ordering: '',
        rated: false,
        unrated_only: false,
      },
      startupCreateModalVisible: false,
      sectorChoices,
      locationChoices: COUNTRY_CHOICES.sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
      presetLocationChoices: [
        'Estonia',
        'Latvia',
        'Lithuania',
        'Czech Republic',
        'Slovakia',
        'Hungary',
        'Poland',
        'Romania',
        'Bulgaria',
        'Slovenia',
        'Croatia',
        'Albania',
        'Bosnia and Herzegovina',
        'Kosovo',
        'North Macedonia',
        'Montenegro',
        'Serbia',
        'Belarus',
        'Moldova',
        'Ukraine',
      ],
      stageChoices,
      productTypes,
      businessTypes,
      interestStatesChoices,
      sharedChoices: [
        { label: 'Shared', value: true },
        { label: 'Not shared', value: false },
      ],
      hasDocumentsChoices: [
        { label: 'Has documents', value: true },
        { label: 'No documents yet', value: false },
      ],
    }
  },
  mounted() {
    if (this.filtersVisible || this.user.isAdmin)
      this.$refs.collapse.setActiveNames(['filters'])

    if (Object.keys(this.$route.query).length > 0) {
      const filtersToLoad = {}
      Object.keys(this.$route.query).forEach((key) => {
        const value = this.$route.query[key]

        if (key.includes('__min')) {
          const realKeyMin = key.toString().substr(0, key.indexOf('__min'))

          if (this.$route.query[`${realKeyMin}__max`]) {
            filtersToLoad[realKeyMin] = key.includes('profile_progress')
              ? [
                  parseInt(value, 10),
                  parseInt(this.$route.query[`${realKeyMin}__max`], 10),
                ]
              : [
                  getCorespondingDataArray(realKeyMin).findIndex(
                    (v) => v.value === parseInt(value, 10),
                  ),
                  getCorespondingDataArray(realKeyMin).findIndex(
                    (v) =>
                      v.value ===
                      parseInt(this.$route.query[`${realKeyMin}__max`], 10),
                  ),
                ]
          } else {
            filtersToLoad[realKeyMin] = key.includes('profile_progress')
              ? [parseInt(value, 10), 100]
              : [
                  getCorespondingDataArray(realKeyMin).findIndex(
                    (v) => v.value === parseInt(value, 10),
                  ),
                  getCorespondingDataArray(realKeyMin).length - 1,
                ]
          }
        }
        if (key.includes('__max')) {
          const realKeyMax = key.toString().substr(0, key.indexOf('__max'))
          if (this.$route.query[`${realKeyMax}__min`]) {
            filtersToLoad[realKeyMax] = key.includes('profile_progress')
              ? [
                  parseInt(this.$route.query[`${realKeyMax}__min`], 10),
                  parseInt(value, 10),
                ]
              : [
                  getCorespondingDataArray(realKeyMax).findIndex(
                    (v) => v.value === parseInt(value, 10),
                  ),
                  getCorespondingDataArray(realKeyMax).findIndex(
                    (v) =>
                      v.value ===
                      parseInt(this.$route.query[`${realKeyMax}__min`], 10),
                  ),
                ]
          } else {
            filtersToLoad[realKeyMax] = key.includes('profile_progress')
              ? [0, parseInt(value, 10)]
              : [
                  0,
                  getCorespondingDataArray(realKeyMax).findIndex(
                    (v) => v.value === parseInt(value, 10),
                  ),
                ]
          }
        }

        if (!key.includes('__min') && !key.includes('__max')) {
          if (key === 'page') filtersToLoad[key] = parseInt(value, 10)
          else filtersToLoad[key] = value
        }

        if (key === 'location' && typeof value === 'string') {
          filtersToLoad[key] = [value]
        }
      })

      this.form = {
        ...this.form,
        ...filtersToLoad,
      }
    }
  },
  computed: {
    allEnabledFilters() {
      const availableFilters = [
        'search',
        'sectors',
        'companyStages',
        'location',
        'previousRounds',
        'currentRound',
        'productTypes',
        'businessTypes',
        'annualRevenue',
        'mrr',
        'publishState',
        'adminActions',
        'extraActions',
        'profileProgress',
      ]
      if (this.enabledFilters === true) {
        const pairs = availableFilters.map((key) => [key, true])
        return fromPairs(pairs)
      } else if (this.enabledFilters === false) {
        return {}
      } else {
        const filteredPairs = this.enabledFilters.filter((f) =>
          availableFilters.includes(f),
        )
        const pairs = filteredPairs.map((f) => [f, true])
        return fromPairs(pairs)
      }
    },
    ...mapState({
      user: (state) => state.user.data,
      filtersVisible: (state) => state.startupList.filtersVisible,
    }),
    orderingOptions() {
      return [
        { value: '', label: 'Name ascending' },
        { value: '-updated_at', label: 'Recently active' },
        { value: '-created_at', label: 'Recently created' },
      ]
    },
  },
  methods: {
    openStartupCreateModal() {
      this.startupCreateModalVisible = true
    },
    closeStartupCreateModal() {
      this.startupCreateModalVisible = false
    },
    onStartupCreated() {
      this.$store.dispatch('startupList/fetch')
    },
    formatRounds(value) {
      return !value ? 0 : roundChoices[value].label
    },
    formatMRR(value) {
      return !value ? 0 : monthlyRecurringRevenueChoices[value].label
    },
    isFilterEnabled(filter) {
      return this.allEnabledFilters[filter]
    },
    toggleFiltersCollapseItemHeader(display) {
      document.getElementsByClassName(
        'el-collapse-item__header',
      )[0].style.display = display
    },
    showFilters(activeNames) {
      if (!activeNames.length) return

      this.toggleFiltersCollapseItemHeader('none')
      this.$store.commit('startupList/showFilters')
    },
    collapseFilters() {
      this.toggleFiltersCollapseItemHeader('flex')
      this.$refs.collapse.setActiveNames([])
      this.$store.commit('startupList/hideFilters')
    },
    emitChanges() {
      const form = { ...this.form }
      if (!form.visited) {
        delete form.visited
      } else {
        form.visited = !form.visited
      }
      if (
        (form.unrated_only && form.rated) ||
        (!form.unrated_only && !form.rated)
      ) {
        delete form.rated
        delete form.unrated_only
      }
      Object.keys(form).forEach((key) => {
        if (typeof form[key] === 'string') {
          form[key] = form[key].trim()
        } else if (
          Array.isArray(form[key]) &&
          form[key].every((i) => typeof i === 'string')
        ) {
          form[key] = form[key].map((i) => i.trim())
        }
      })
      this.$emit('change', form)
      if (!form.ordering) {
        delete form.ordering
      }
      console.log(form)
    },
    resetFilters() {
      this.form = emptyFilterForm()
      this.emitChanges()
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.filters__form {
  padding: 25px 25px 15px;

  .el-form-item {
    width: 25%;
    padding: 0 10px;

    @media screen and (max-width: 1080px) {
      max-width: 800px;
      min-width: 276px;
    }
  }

  .el-form-item__label {
    line-height: 20px;
    padding: 0;
  }

  // do not show green border at filters
  .el-form-item.is-success .el-input__inner,
  .el-form-item.is-success .el-input__inner:focus,
  .el-form-item.is-success .el-textarea__inner,
  .el-form-item.is-success .el-textarea__inner:focus {
    border-color: #dcdfe6;
  }

  .el-select,
  .el-button {
    width: 100%;
  }

  .actions,
  .search-form-item {
    .el-form-item__label {
      visibility: hidden;
    }
  }

  .el-collapse {
    border: none;

    @media screen and (max-width: 1080px) {
      max-width: 800px;
    }
  }

  .el-collapse-item__wrap {
    background-color: $grey180;
    border-bottom: none;
  }

  .el-collapse-item__content {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    padding-bottom: 0px;
  }

  .el-collapse-item__header {
    margin-right: 14px;
    display: flex;
    justify-content: flex-end;
    font-weight: 500;
    color: $linkColor;
    background-color: $grey180;

    .el-collapse-item__arrow {
      margin: 0 8px;
      color: initial;
    }
  }

  .el-slider {
    max-width: 94%;
    margin: 0 auto;
  }
}
</style>

<style scoped lang="scss">
@import '~assets/css/colors.scss';

.filters {
  .dropdown-menu {
    max-height: 600px;
    overflow: auto;
  }
}

.filters__row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  @apply justify-start;
}

.filters-actions__fill {
  min-width: 75%;

  @media screen and (max-width: 1080px) {
    display: none;
  }
}

.filters-actions__container {
  width: 25%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: 1080px) {
    align-items: initial;
  }
}

.filters__form {
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 1080px) {
    align-items: center;
  }
}

.filters-action-link {
  margin-right: 12px;
}

.main-filters__container {
  display: flex;
  flex-wrap: wrap;
  @apply justify-start;

  @media screen and (max-width: 1080px) {
    max-width: 800px;
  }
}

.form-control {
  border: 1px solid $grey150;
  font-weight: 100;

  &::placeholder {
    color: $grey60;
  }
}

.form-group label {
  font-weight: 300;
}

.filters-extra {
  .btn {
    margin-top: 26px;
  }
  text-align: end;
  margin-bottom: 16px;
}

.filters-actions {
  padding: 0 4px;
  margin-top: 24px;
  width: 100%;
  display: flex;

  @media screen and (max-width: 1080px) {
    width: 276px;
    padding: initial;
    align-items: initial;
  }
}
</style>
